<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import useVuelidate from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Skeleton from 'primevue/skeleton';
import Message from 'primevue/message';
import { useRouting } from '@/composables';
import { useTenantIntegrationStore } from '@/store';

interface ResonantEdFormIntegration {
  id?: number;
  bearerToken: string;
  apiUrl: string;
}

const RESONANT_ED_PROVIDER_SLUG = 'screeners.resonant_ed';

const tenantIntegrationStore = useTenantIntegrationStore();

const { loading, error } = storeToRefs(tenantIntegrationStore);

const $routing = useRouting();

const rules = {
  bearerToken: { required },
  apiUrl: { required, url },
};

const payload = ref<ResonantEdFormIntegration>({
  bearerToken: '',
  apiUrl: 'https://resonant.resonanteducation.com',
});

const v$ = useVuelidate(rules, payload);

const originalForm = ref<ResonantEdFormIntegration | null>(null);

const tenantId = ref(Number($routing.route.params.id));

const integration = ref();

const isLoading = computed(() => loading.value);

onMounted(async () => await init());

const init = async () => {
  integration.value = tenantIntegrationStore.findIntegration(RESONANT_ED_PROVIDER_SLUG);

  if (integration.value) {
    payload.value = {
      id: integration.value.id,
      bearerToken: integration.value.data.bearer_token || '',
      apiUrl: integration.value.data.api_url || 'https://resonant.resonanteducation.com',
    };

    originalForm.value = { ...payload.value };
  }
};

const resetDirty = () => v$.value.$reset();

const fieldStyles = (key: string, styles?: string): string => {
  const validation = v$.value[key].$invalid && v$.value[key].$dirty ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

const onCancel = () => {
  if (originalForm.value) {
    payload.value = { ...originalForm.value };
  }
  v$.value.$reset();
};

const onSave = async () => {
  if (v$.value.$invalid) return;

  const formData = new FormData();
  const configData = JSON.stringify({
    bearer_token: payload.value.bearerToken,
    api_url: payload.value.apiUrl,
  });

  formData.append('data', configData);
  formData.append('integration_provider_type_slug', RESONANT_ED_PROVIDER_SLUG);

  payload.value.id
    ? await tenantIntegrationStore.updateTenantIntegration(tenantId.value, payload.value.id, formData)
    : await tenantIntegrationStore.saveTenantIntegration(tenantId.value, formData);

  if (!error.value) {
    await init();
    originalForm.value = { ...payload.value };
    resetDirty();
  }
};
</script>

<template>
  <FormCard
    title="Resonant Ed Screeners"
    :dirty="v$.$anyDirty"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="v$.$anyDirty && v$.$invalid"
  >
    <template #form>
      <form id="resonant_ed_form" class="w-full">
        <div class="field">
          <label for="api-url">
            API URL
            <span class="text-red-500">*</span>
          </label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="api-url"
            v-model="v$.apiUrl.$model"
            placeholder="https://resonant.resonanteducation.com"
            :class="fieldStyles('apiUrl', 'w-full')"
            @blur="v$.apiUrl.$touch"
          />
          <small v-if="v$.apiUrl.$invalid && v$.apiUrl.$dirty" class="p-error">A valid URL is required</small>
        </div>
        <div class="field">
          <label for="bearer-token">
            Bearer Token
            <span class="text-red-500">*</span>
          </label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Password
            v-else
            id="bearer-token"
            v-model="v$.bearerToken.$model"
            placeholder="Enter Bearer Token"
            :class="fieldStyles('bearerToken', 'w-full')"
            :feedback="false"
            toggle-mask
            @blur="v$.bearerToken.$touch"
          />
          <small v-if="v$.bearerToken.$invalid && v$.bearerToken.$dirty" class="p-error">
            Bearer Token is required
          </small>
        </div>
        <div class="field">
          <Message severity="info" :closable="false">
            <p class="text-sm">
              The Bearer Token and API URL are used to authenticate with the Resonant Ed API. These credentials will be
              encrypted and stored securely.
            </p>
          </Message>
        </div>
      </form>
    </template>
  </FormCard>
</template>
